import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Keyboard, View } from 'react-native';
import { emojify } from 'node-emoji';
import { AppView } from 'design-system/AppView';
import EmojiText from 'design-system/EmojiText';
import { CardView } from 'design-system/CardView';
import Button, { HEIGHT } from 'design-system/Button';
import { rem } from 'design-system/values';
import { IconEmma, IconText } from 'design-system/icons';
import EmojiPickerModal from 'design-system/emojiPicker/EmojiPickerModal';
import { AnimatedKeyboardAwareScrollView } from 'design-system/AnimatedComponents';
import { SettingsItemInput, SettingsItemNavigate, } from 'design-system/SettingsItem';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useColors from 'hooks/useColors';
import Alert from 'utils/packages/Alert';
import { useKeyboardAdjustPan } from 'hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { webPickerWidth } from 'features/emoji-picker/components/EmojiSelector';
import { IconCategory } from 'design-system/icons/IconCategory';
import { useAppSelector } from 'store/hooks';
import checkFeatureFlag from 'utils/featureFlags';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { isWeb } from '../../../constants';
import WebEmojiPicker from '../components/WebEmojiPicker.web';
import CustomCategorySuggestions from '../components/categories/CustomCategorySuggestions';
import IconTransactionCategory from '../icons/IconTransactionCategory';
const CreateCustomCategoryScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const showSubcategoriesCreateFlow = useAppSelector((store) => checkFeatureFlag(store.utils.featureFlags, 'show_subcategories_create_flow'));
    const colors = useColors();
    const nameInputRef = useRef(null);
    const emojiViewRef = useRef(null);
    useAutoFocusInput(nameInputRef);
    useKeyboardAdjustPan();
    const { paddingHorizontalStyle, sidebarWidth, width } = useAppFrameDimensions(isWeb);
    const [webEmojiPickerState, setWebEmojiPickerState] = useState(undefined);
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const { parentCategory: parentCategoryParam } = route.params || {};
    const [name, setName] = useState('');
    const [emoji, setEmoji] = useState('');
    const [parentCategory, setParentCategory] = useState(parentCategoryParam);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<HelpScreenButton articlesKey={HELP_ARTICLES_KEY.CUSTOM_CATEGORIES}/>),
        });
    }, []);
    const [suggestedData, setSuggestedData] = useState();
    const clearWebEmojiPickerState = useCallback(() => {
        setIsEmojiPickerOpen(false);
        setWebEmojiPickerState(undefined);
    }, []);
    const onChangeName = useCallback((name) => {
        setName(name);
        setSuggestedData(undefined);
    }, []);
    const handleSuggestionPress = useCallback((name, emoji) => {
        Keyboard.dismiss();
        setName(name);
        setEmoji(emoji || '');
        clearWebEmojiPickerState();
        setSuggestedData({ name, emoji });
    }, [clearWebEmojiPickerState]);
    const onEmojiModalClose = useCallback(() => {
        setIsEmojiPickerOpen(false);
    }, []);
    const onSetEmoji = useCallback((emoji) => {
        setEmoji(emoji);
        setWebEmojiPickerState(undefined);
        onEmojiModalClose();
    }, [onEmojiModalClose]);
    const onPressEmoji = useCallback(() => {
        Keyboard.dismiss();
        setIsEmojiPickerOpen(true);
        if (isWeb) {
            emojiViewRef.current?.measure((_, __, ___, ____, fx, fy) => {
                setWebEmojiPickerState({
                    fx: fx - sidebarWidth + (width - webPickerWidth - rem(64)),
                    fy,
                });
            });
        }
        else {
            EmojiPickerModal.show(emoji || undefined, onSetEmoji, onEmojiModalClose);
        }
    }, [emoji, onEmojiModalClose, onSetEmoji, sidebarWidth, width]);
    const onContinue = useCallback(() => {
        clearWebEmojiPickerState();
        const displayName = name.trim();
        if (!displayName.length) {
            Alert.alert(emojify('Hold on :dizzy:'), 'You must enter a name.');
            return;
        }
        if (!emoji) {
            Alert.alert(emojify('Hold on :dizzy:'), 'You must choose an emoji.');
            return;
        }
        const category = {
            displayName,
            emoji,
            parentCategoryId: parentCategory?.id,
        };
        // Skip the screen to choose color and go straight to the created screen
        navigation.navigate(parentCategory?.id !== undefined
            ? 'CustomCategoryCreated'
            : 'CustomCategoryChooseColor', {
            category,
            keyBack: route.key,
        });
    }, [
        clearWebEmojiPickerState,
        emoji,
        name,
        parentCategory?.id,
        route.key,
    ]);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT + rem(16), paddingHorizontalStyle);
    const onPressParentCategory = useCallback(() => {
        Keyboard.dismiss();
        navigation.navigate('PickCategory', {
            hideExcluded: true,
            hideSubcategories: true,
            title: 'Pick a parent category',
            subtitle: 'Choose “None” if you want this to be a main category',
            category: parentCategory || {
                id: 'none',
                displayName: 'None',
                emoji: null,
            },
            onPickCategory: (data) => {
                if (!data) {
                    setParentCategory(undefined);
                }
                else {
                    const selectedCategory = {
                        id: data.id,
                        displayName: data.displayName || '',
                        color: 'color' in data ? data.color : undefined,
                        emoji: 'emoji' in data ? data.emoji : null,
                    };
                    setParentCategory(selectedCategory);
                }
                navigation.pop();
            },
        });
    }, [parentCategory]);
    return (<AppView type="secondary" withFrame={false}>
          <AnimatedKeyboardAwareScrollView scrollEventThrottle={16} showsVerticalScrollIndicator={false} onScroll={scrollHandler} contentContainerStyle={paddingBottom} keyboardShouldPersistTaps="handled">
            <CardView webPadding noMarginBottom>
              <SettingsItemInput description="Name" icon={<IconText gradientKey="darkPink"/>} value={name} onChangeText={onChangeName} placeholder="Name your category..." iconSize={44} ref={nameInputRef} returnKeyType="done" {...(isWeb && {
        style: cursorStyle,
        onPress: clearWebEmojiPickerState,
    })}/>
              <View ref={emojiViewRef}>
                <SettingsItemNavigate title={emoji ? 'Change' : 'Choose...'} description="Emoji" icon={emoji ? (<EmojiText fontSize={20}>{emoji}</EmojiText>) : (<IconEmma />)} iconSize={44} onPress={onPressEmoji} isSelected={isEmojiPickerOpen}/>
              </View>
              {!!showSubcategoriesCreateFlow && (<SettingsItemNavigate description="Parent category (optional)" title={parentCategory?.displayName || 'None'} icon={parentCategory ? (<IconTransactionCategory width={rem(24)} height={rem(24)} category={parentCategory}/>) : (<IconCategory alt color={colors.categories.custom}/>)} onPress={onPressParentCategory} isSelected={false}/>)}
            </CardView>
            <CustomCategorySuggestions name={name} handleSuggestionPress={handleSuggestionPress} suggestedData={suggestedData} parentCategory={parentCategory}/>
          </AnimatedKeyboardAwareScrollView>
          <Button brand floating title="Continue" onPress={onContinue}/>
          {isWeb ? (<WebEmojiPicker {...webEmojiPickerState} onEmojiSelected={onSetEmoji} defaultSelectedEmoji={emoji || undefined} clearWebEmojiPickerState={clearWebEmojiPickerState}/>) : null}
        </AppView>);
}, ({ colors }) => ({
    title: 'Create category',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const cursorStyle = {
    margin: 0,
    paddingVertical: rem(16),
    paddingHorizontal: rem(8),
};
export default CreateCustomCategoryScreen;
